import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Modal,
  TextField,
  MenuItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  FormControlLabel,
  Switch,
  FormLabel
} from '@mui/material';
import { CloudUpload, RemoveRedEye, Delete, InsertDriveFile, Close } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDropzone } from 'react-dropzone';
import { getDocumentsByTransportLine, uploadDocument, deleteDocument } from '../../api';

const DocumentManagement = ({ selectedLine }) => {
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [documentCategory, setDocumentCategory] = useState('legal');
  const [expiryDate, setExpiryDate] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Document type options by category
  const documentTypeOptions = {
    legal: [
      'Acta constitutiva',
      'Poder Notarial',
      'Identificacion Oficial',
      'Comprobante de domicilio', 
      'Constancia de situacion fiscal',
      'Opinion de cumplimiento',
      'Caratula bancaria',
      'Alta de proveedor',
      'Convenio de confidencialidad',
      'Formato de proveedores'
    ],
    patrimonial: [
      'Tarjeta de circulacion Tracto',
      'Tarjeta de circulacion Remolque',
      'Poliza de seguro Tracto',
      'Poliza de seguro Remolque',
      'Fisico-Mecanica',
      'Registro Patronal',
      'Licencia de operador',
      'Contrato GPS'
    ]
  };
  
  // Helper function to determine document category based on document type
  const getDocumentCategory = (docType) => {
    if (documentTypeOptions.legal.includes(docType)) {
      return 'Legal';
    } else if (documentTypeOptions.patrimonial.includes(docType)) {
      return 'Patrimonial';
    }
    return 'No especificado';
  };

  // Document types that require expiry date
  const documentsWithExpiryDate = [
    'Poliza de seguro Tracto',
    'Poliza de seguro Remolque',
    'Fisico-Mecanica',
    'Licencia de operador',
    'Contrato GPS'
  ];
  
  // Check if the current document type requires expiry date
  const requiresExpiryDate = documentsWithExpiryDate.includes(documentType);

  // Reset expiry date when switching to a document type that doesn't need it
  useEffect(() => {
    if (!requiresExpiryDate) {
      setExpiryDate('');
    }
  }, [requiresExpiryDate]);

  useEffect(() => {
    if (selectedLine) {
      fetchDocuments();
    }
  }, [selectedLine]);

  // Reset document type when category changes
  useEffect(() => {
    setDocumentType('');
  }, [documentCategory]);

  const fetchDocuments = async () => {
    try {
      const data = await getDocumentsByTransportLine(selectedLine.id);
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleUpload = async () => {
    if (!selectedFile || !selectedLine || !documentType) return;
    
    // Validate expiry date for documents that require it
    if (requiresExpiryDate && !expiryDate) {
      toast.error('Este tipo de documento requiere una fecha de vencimiento');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', documentType);
    formData.append('document_category', documentCategory);
    
    // Only append expiry_date if it has a value
    if (expiryDate) {
      // Format date to ensure it's in YYYY-MM-DD format
      const formattedDate = new Date(expiryDate).toISOString().split('T')[0];
      formData.append('expiry_date', formattedDate);
    }
    
    formData.append('fileName', `${documentType}_${selectedLine.name}`);
    formData.append('folderId', '19QrUVBIdb-dUzXOxV3E36hDfVl1mFugL');
    formData.append('linea_transporte_id', selectedLine.id);

    setIsUploading(true);
    try {
      await uploadDocument(formData);
      toast.success('Documento subido correctamente');
      fetchDocuments();
      setIsModalOpen(false);
    } catch (error) {
      // Improved error message for date parsing issues
      if (error.response?.data?.detail?.[0]?.type === "datetime_from_date_parsing") {
        toast.error('Error en el formato de fecha. Verifique que la fecha sea válida.');
      } else {
        toast.error(`Error al subir el documento: ${
          error.response?.data?.detail || 'Error desconocido'
        }`);
      }
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setDocumentType('');
      setExpiryDate('');
      setDocumentCategory('legal');
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const confirmDeleteDocument = (docId) => {
    setDeletingDocId(docId);
    toast.info(
      <div>
        <p>¿Está seguro de eliminar este documento?</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
          <Button 
            variant="contained" 
            color="error" 
            size="small"
            onClick={() => {
              // Call the delete function directly from here to ensure it's triggered
              handleDeleteConfirmed(docId);
              toast.dismiss();
            }}
          >
            Eliminar
          </Button>
          <Button 
            variant="contained" 
            color="inherit" 
            size="small"
            onClick={() => {
              setDeletingDocId(null);
              toast.dismiss();
            }}
          >
            Cancelar
          </Button>
        </div>
      </div>,
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
      }
    );
  };

  const handleDeleteConfirmed = async (docId) => {
    // Use the passed docId parameter instead of relying on state
    const idToDelete = docId || deletingDocId;
    
    if (!idToDelete) {
      console.error("No document ID to delete");
      return;
    }
    
    try {
      await deleteDocument(idToDelete);
      toast.success('Documento eliminado correctamente');
      fetchDocuments();
    } catch (error) {
      toast.error(`Error al eliminar el documento: ${error.response?.data?.detail || 'Error desconocido'}`);
    } finally {
      setDeletingDocId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Try to parse the date string
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date:", dateString);
      return 'Fecha inválida';
    }
    
    return date.toLocaleDateString();
  };

  const handleResetModal = () => {
    setSelectedFile(null);
    setDocumentType('');
    setExpiryDate('');
    setDocumentCategory('legal');
  };

  const handleOpenModal = () => {
    handleResetModal();
    setIsModalOpen(true);
  };

  if (!selectedLine) {
    return (
      <Typography variant="h6" align="center">
        
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Gestión de Documentos
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenModal}
        startIcon={<CloudUpload />}
      >
        Añadir Documento
      </Button>
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...modalStyle }}>
          <Typography variant="h6" gutterBottom>
            Subir Documento
          </Typography>
          
          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">Categoría del Documento</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={documentCategory === 'patrimonial'}
                  onChange={(e) => setDocumentCategory(e.target.checked ? 'patrimonial' : 'legal')}
                  name="documentCategory"
                  color="primary"
                />
              }
              label={documentCategory === 'patrimonial' ? 'Patrimonial' : 'Legal'}
            />
          </FormControl>
          
          <TextField
            label="Tipo de Documento"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            select
            fullWidth
            margin="normal"
          >
            {documentTypeOptions[documentCategory].map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          
          {requiresExpiryDate && (
            <TextField
              label="Fecha de Vencimiento"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              fullWidth
              margin="normal"
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
          
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed gray',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              mt: 2,
              position: 'relative'
            }}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography>Drop the files here ...</Typography>
            ) : (
              !selectedFile && <Typography>Carga de archivos</Typography>
            )}
            {selectedFile && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ListItemIcon>
                  <InsertDriveFile />
                </ListItemIcon>
                <ListItemText
                  primary={selectedFile.name.length > 20 ? `${selectedFile.name.substring(0, 20)}...` : selectedFile.name}
                />
                <IconButton onClick={handleRemoveFile}>
                  <Close />
                </IconButton>
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || (requiresExpiryDate && !expiryDate)}
            startIcon={<CloudUpload />}
            sx={{ mt: 2 }}
          >
            {isUploading ? <CircularProgress size={24} /> : 'Subir Documento'}
          </Button>
        </Box>
      </Modal>
      <Box mt={2}>
        <Typography variant="subtitle1">Documentos Subidos:</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Fecha de Carga</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.document_type}</TableCell>
                  <TableCell>{getDocumentCategory(doc.document_type)}</TableCell>
                  <TableCell>{formatDate(doc.upload_date)}</TableCell>
                  <TableCell>{formatDate(doc.expiry_date)}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.is_valid ? 'Vigente' : 'Vencido'}
                      color={doc.is_valid ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton href={`https://drive.google.com/file/d/${doc.file_path}/view`} target="_blank">
                      <RemoveRedEye />
                    </IconButton>
                    <IconButton color="error" onClick={() => confirmDeleteDocument(doc.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <ToastContainer />
    </Box>
  );
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export default DocumentManagement;
